import { InfoPage } from '@/components/info-page';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';

import { zodResolver } from '@hookform/resolvers/zod';
import { ComponentProps, ReactNode, useState } from 'react';
import { DefaultValues, FieldValues, UseFormReturn, useForm } from 'react-hook-form';

import { z } from 'zod';

export interface SubmitProps<FormData extends FieldValues> {
  formData: FormData;
  disableEditing: () => void;
}

interface EditableInfoPageProps<S extends z.ZodRawShape> extends Omit<ComponentProps<typeof InfoPage>, 'children'> {
  defaultValues?: DefaultValues<z.infer<z.ZodObject<S>>>;
  onSubmit?: (submitProps: SubmitProps<z.infer<z.ZodObject<S>>>) => unknown;
  children: (props: { form: UseFormReturn<z.infer<z.ZodObject<S>>>; editing: boolean }) => ReactNode;
  formSchema: z.ZodObject<S>;
  readonly?: boolean;
  shouldResetOnCancel?: boolean;
}

export function EditableInfoPage<S extends z.ZodRawShape>({
  children,
  headerActions = <></>,
  defaultValues,
  onSubmit,
  formSchema,
  readonly,
  shouldResetOnCancel = true,
  ...otherInfoPageProps
}: EditableInfoPageProps<S>) {
  const [editing, setEditing] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { isSubmitting } = form.formState;
  const innerInfoPage = (
    <InfoPage
      {...otherInfoPageProps}
      headerActions={
        <>
          {headerActions}

          {!readonly && (
            <>
              {editing && (
                <Button
                  onClick={() => {
                    if (shouldResetOnCancel) {
                      form.reset();
                    }
                    setEditing(false);
                  }}
                  size="md"
                  variant="dismiss"
                  loading={isSubmitting}
                  data-testid={
                    otherInfoPageProps['data-testid'] ? `${otherInfoPageProps['data-testid']}-button-cancel` : undefined
                  }
                >
                  Cancel
                </Button>
              )}

              {editing && (
                <Button
                  type="submit"
                  size="md"
                  variant="destructive"
                  loading={isSubmitting}
                  data-testid={
                    otherInfoPageProps['data-testid'] ? `${otherInfoPageProps['data-testid']}-button-save` : undefined
                  }
                >
                  Save
                </Button>
              )}

              {!editing && (
                <Button
                  onClick={() => setEditing(true)}
                  size="md"
                  variant="destructive"
                  data-testid={
                    otherInfoPageProps['data-testid'] ? `${otherInfoPageProps['data-testid']}-button-edit` : undefined
                  }
                >
                  Edit
                </Button>
              )}
            </>
          )}
        </>
      }
    >
      <>{children({ form, editing: readonly ? false : editing })}</>
    </InfoPage>
  );

  if (!editing) {
    // do not wrap in <form> when not editing to avoid other modals on page submitting this form on submit
    return innerInfoPage;
  }

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((formData) => {
            return onSubmit?.({
              formData,
              disableEditing: () => setEditing(false),
            });
          })}
        >
          {innerInfoPage}
        </form>
      </Form>
    </>
  );
}
